import React, { useState } from 'react';

import posed from 'react-pose';
import styled from 'styled-components';

import IconClose from 'components/icons/close/';
import { IAccordionEntry, IAccordionWidget, ILocaleString, Maybe } from 'generated/sanity-graphql';

import theme from './theme';

const AnimArrow = posed.div({
  open: {
    transform: 'rotate(0deg)',
  },
  closed: {
    transform: 'rotate(-45deg)',
  },
});

const AnimArrowDiv = styled(AnimArrow)`
  position: relative;
  top: 1.25rem;
  height: 2rem;
  width: 2rem;
  display: block;
  transform-origin: 50% 50%;
  float: right;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArrowDown = styled(({ isExpanded, ...props }) => <IconClose {...props} />)`
  fill: ${props =>
    props.isExpanded ? theme.expandedAccordionActiveAccentColor : Styles.color.black};
  display: block;
  height: 2rem;
  width: 2rem;
`;

const AccordionTitle = styled.h2``;
const AccordionWrapper = styled.ul`
  margin: 0 auto;
  width: calc(90% + 40px);
  max-width: calc(730px + 40px);
  list-style: none;
  padding: 2.5rem 0rem;
  ${Styles.mobile`
    padding: 1.5rem 0rem;
  `}
  ${AccordionTitle} {
    padding: 0px 20px;
  }
`;

const CollapsedContent = styled.button`
  width: calc(100% - 2.25rem);
  padding: 0;
  border: 0 none;
  background-color: inherit;
  font: inherit;
  text-align: left;
`;

const ExpandedContent = styled.p`
  margin: 0;
  padding: 0;
  float: left;
  width: calc(100% - 2.25rem);
  color: ${Styles.color.grey.two};
`;

const ContentContainer = styled.h3`
  font-size: 1.125rem;
  margin: 1.496rem 0;
`;

const AccordionItem = styled.li<{ isExpanded: boolean }>`
  box-sizing: border-box;
  padding: 0 1.25rem 0 1rem;
  list-style: none;
  overflow: hidden;
  cursor: pointer;
  background-color: ${props => (props.isExpanded ? theme.expandedAccordionBackground : 'none')};
  &:hover {
    background-color: ${theme.accordionHoverBackground};
  }
  & + & {
    border-top: 1px solid ${Styles.color.grey.four};
  }
  ${CollapsedContent} {
    color: ${props => (props.isExpanded ? theme.expandedAccordionActiveAccentColor : 'inherit')};
    font-weight: ${props => (props.isExpanded ? theme.expandedAccordionFontWeight : '300')};
  }

  ${ExpandedContent} {
    margin: ${props => (props.isExpanded ? '0 0 1.33em 0' : 'inherit')};
  }
`;

const Animated = posed(ExpandedContent)({
  open: {
    height: 'auto',
    opacity: 1,
    transition: { duration: 333, ease: 'easeInOut' },
  },
  closed: {
    height: '1px',
    opacity: 0,
    transition: { duration: 333, ease: 'easeInOut' },
  },
});

type StringType<T> = {
  [K in keyof T]: T[K] extends Maybe<ILocaleString> ? { locale: string } : T[K];
};

export type IAccordionWidgetProps = StringType<
  Required<Pick<IAccordionWidget, 'accordionTitle'>>
> & {
  accordionContent: Array<
    StringType<Pick<IAccordionEntry, 'collapsedContent' | 'expandedContent'>>
  >;
};

const AccordionWidget: React.FC<IAccordionWidgetProps> = props => {
  const [expanded, setExpanded] = useState(-1);

  return (
    <AccordionWrapper>
      <AccordionTitle>{props.accordionTitle?.locale}</AccordionTitle>
      {props.accordionContent.map((c, i: number) => {
        let isExpanded = expanded === i;
        return (
          <AccordionItem
            key={`accordionItem${i}`}
            isExpanded={isExpanded}
            onClick={() => {
              setExpanded(isExpanded ? -1 : i);
            }}
          >
            <AnimArrowDiv pose={isExpanded ? 'open' : 'closed'}>
              <ArrowDown isExpanded={isExpanded} />
            </AnimArrowDiv>
            <ContentContainer>
              <CollapsedContent
                id={`accordionButton${i}`}
                aria-controls={`accordionContent${i}`}
                aria-expanded={isExpanded ? 'true' : 'false'}
              >
                {c.collapsedContent?.locale}
              </CollapsedContent>
            </ContentContainer>
            <Animated
              pose={isExpanded ? 'open' : 'closed'}
              id={`accordionContent${i}`}
              role="region"
              aria-labelledby={`accordionButton${i}`}
            >
              {c.expandedContent?.locale}
            </Animated>
          </AccordionItem>
        );
      })}
    </AccordionWrapper>
  );
};

export default AccordionWidget;
